import React, { useEffect, useState } from 'react';
import api from '../services/api';
import './LoginForm.css';

function LoginForm({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    // Check URL query params for auth denial (e.g., /login?auth=denied&reason=not_allowed)
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('auth') === 'denied' && params.get('reason') === 'not_allowed') {
        if (isMounted) setError('Invalid Credentials');
        // Optional: clean the URL so message is not re-triggered on refresh
        params.delete('auth');
        params.delete('reason');
        params.delete('email');
        const cleaned = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        window.history.replaceState({}, '', cleaned);
      }
    } catch (e) {
      // ignore URL parsing errors
    }
    const fetchProviders = async () => {
      try {
        const response = await api.get('auth/providers/');
        if (!isMounted) return;
        const list = response?.data?.providers ?? [];
        setProviders(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Unable to load social providers', err);
        if (isMounted) setProviders([]);
      } finally {
        if (isMounted) setProvidersLoading(false);
      }
    };

    fetchProviders();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!username || !password) {
      setError('Please enter a username and password.');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      // Fetch fresh CSRF token and wait for cookie to be set
      await api.get('auth/csrf/');
      // Re-read the CSRF token from the updated cookie
      await new Promise(r => setTimeout(r, 100));
      const response = await api.post('auth/login/', { username, password });
      if (response?.data?.user) {
        onLogin(response.data.user);
      } else {
        setError('Unable to log in.');
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Login failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (loginUrl) => {
    if (!loginUrl) return;
    window.location.href = loginUrl;
  };

  return (
    <div className="login-card">
      <h2>Sign in to XBRL Converter</h2>
      <form className="login-form" onSubmit={handleSubmit}>
        <label htmlFor="username">Username</label>
        <input
          id="username"
          type="text"
          value={username}
          onChange={e => setUsername(e.target.value)}
          autoComplete="username"
          disabled={loading}
          placeholder="Enter your username"
        />

        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoComplete="current-password"
          disabled={loading}
          placeholder="Enter your password"
        />

        {error && <div className="login-error">{error}</div>}

        <button type="submit" className="login-btn" disabled={loading}>
          {loading ? 'Signing in\u2026' : 'Sign in'}
        </button>
      </form>

      {!providersLoading && providers.length > 0 && (
        <div className="social-section">
          <div className="social-divider" role="presentation">
            <span>or continue with</span>
          </div>
          <div className="social-buttons">
            {providers.map(provider => {
              const isGoogle =
                provider.id === 'google' ||
                (provider.name && provider.name.toLowerCase().includes('google'));
              if (isGoogle) {
                return (
                  <button
                    key={provider.id}
                    type="button"
                    className="gsi-material-button"
                    onClick={() => handleSocialLogin(provider.login_url)}
                    aria-label={provider.button_text || `Continue with ${provider.name}`}
                  >
                    <div className="gsi-material-button-state"></div>
                    <div className="gsi-material-button-content-wrapper">
                      <div className="gsi-material-button-icon" aria-hidden="true">
                        <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlnsXlink="http://www.w3.org/1999/xlink" style={{ display: 'block' }}>
                          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
                          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
                          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
                          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
                          <path fill="none" d="M0 0h48v48H0z"></path>
                        </svg>
                      </div>
                      <span style={{ display: 'none' }}>Sign in with Google</span>
                    </div>
                  </button>
                );
              }
              return (
                <button
                  key={provider.id}
                  type="button"
                  className="social-btn"
                  style={{
                    background: provider.brand_color || '#1f2937',
                    color: provider.text_color || '#ffffff',
                  }}
                  onClick={() => handleSocialLogin(provider.login_url)}
                  aria-label={provider.button_text || `Continue with ${provider.name}`}
                >
                  {provider.button_text || provider.name}
                </button>
              );
            })}
           </div>
        </div>
      )}
    </div>
  );
}

export default LoginForm;
